/* eslint-disable @typescript-eslint/no-dynamic-delete */
import { JOB_SHARE_TYPE, JOB_SPECIAL_CODE, JOB_TYPE } from '@src/enums';
import { PAGE_NO, SOURCE_TYPE } from '@common/const';
import { ICode } from '@src/types';

// 对包含二级分类的职位类别&招聘类型进行处理
export const handleContainSubcode = (selectedList: string[], defaultValue, separator = '_') => {
  if (!selectedList || (Array.isArray(selectedList) && selectedList.length === 0)) {
    return defaultValue;
  }
  const temp: { code: string; subCode: string[] }[] = [];
  selectedList.forEach((item) => {
    const [code, subCode] = item.split(separator);
    const exit = temp.find((it) => it.code === code);
    if (exit) {
      if (subCode) exit.subCode.push(subCode);
    } else {
      temp.push({
        code,
        subCode: subCode ? [subCode] : [],
      });
    }
  });

  // jfjg过滤全部
  temp.forEach((it) => {
    it.subCode = it.subCode.filter((v) => v !== '-1');
  });

  return temp;
};

// 先decodeURIComponent解码参数，在通过JSON.parse解析参数
export function decodeAndParse(params: string | undefined, defaultValue) {
  if (!params) {
    return defaultValue;
  }
  try {
    return JSON.parse(decodeURIComponent(params));
  } catch (error) {
    console.error(error);
    return defaultValue;
  }
}

// 字符串转数组
export function string2Array(str: string | undefined, defaultValue, separator?: string) {
  if (!str) {
    return defaultValue;
  }
  if (!separator) {
    separator = ',';
  }
  try {
    let tempArr = str.split(separator);
    // 处理移动端其他城市
    if (tempArr.includes('other')) {
      tempArr = tempArr.filter((code) => code !== 'other');
    }
    return tempArr;
  } catch (error) {
    console.error(error);
    return defaultValue;
  }
}

// 格式化jfjglist，初始化增加展开折叠标识
export function formatJfJgList(list, selectedList: string[] = [], separator = '_') {
  if (!Array.isArray(list)) return [];
  if (list.length === 0) return [];
  // 对selectedList去重, 只保留一级分类id
  const uniqueCodeList: string[] = [];
  if (selectedList.length > 0) {
    selectedList.forEach((item) => {
      const [code] = item.split(separator);
      if (!uniqueCodeList.includes(code)) {
        uniqueCodeList.push(code);
      }
    });
  }
  return list.map((item) => ({
    ...item,
    unfold: uniqueCodeList.includes(item.code),
  }));
}

/**
 * 自动添加或删除value到url的query对象；
 * @queryObj 这个url上的参数对象，当queryKey的值没有时会删除queryObj上的这个字段
 * @queryKey 参数的key
 * @value 是否要添加到url上的值，通常是某一项的code
 * @separator queryKey的值拼接的分割符号
 */
export function makeUpQueryObj(
  queryObj: object,
  queryKey: string,
  value: string,
  firstCode?: string,
  separator?: string,
) {
  if (!separator) {
    separator = ',';
  }
  let list: string[] = [];
  // 读取queryObj的queryKey，根据separator，分割queryKey的值
  if (queryObj?.[queryKey]) {
    list = string2Array(queryObj[queryKey], [], separator);
  }
  // jfjg全部处理逻辑
  if (firstCode) {
    // 选了全部，其他不选中
    if (value.endsWith('_-1')) {
      list = list.filter((it) => !(it.startsWith(`${firstCode}_`) && !it.endsWith(`${firstCode}_-1`)));
      // 选了其他，全部不选中
    } else {
      list = list.filter((it) => it !== `${firstCode}_-1`);
    }
  }
  // 判断value是否在queryKey的值里，有则删除，没有则添加
  const indexNum = list.findIndex((code) => code === value);

  if (indexNum > -1) {
    // 存在
    list.splice(indexNum, 1);
  } else {
    // 不存在
    list.push(value);
  }

  // 最后判断queryKey是否还有值
  let obj = {};
  if (list.length === 0) {
    // 没有值时
    delete queryObj[queryKey];
    obj = queryObj;
  } else {
    // 有值
    obj = Object.assign(queryObj, { [queryKey]: list.join(separator) });
  }
  // 其他筛选条件需重置pageNo
  delete obj[PAGE_NO];
  return obj;
}

/**
 * 了解美团-我们的团队 更新Bg到url上
 * @queryObj 这个url上的参数对象，当queryKey的值没有时会删除queryObj上的这个字段
 * @queryKey 参数的key
 * @value 是否要添加到url上的值，通常是某一项的code
 * @firstRef 是否首次渲染+刷新页面
 * @separator queryKey的值拼接的分割符号
 */
export function updateBg2Query(
  queryObj: object,
  queryKey: string,
  value: string,
  firstRef?: boolean,
  separator?: string,
) {
  if (!separator) {
    separator = ',';
  }

  const list: string[] = [value];
  const obj = Object.assign(queryObj, { [queryKey]: list.join(separator) });
  // 点击了事业部
  if (!firstRef) {
    const queryArr = ['cityList', 'searchCity', 'jfJgList', 'hiringType', 'pageNo', 'bgSubcode'];
    Object.keys(obj).forEach((key) => {
      if (queryArr.includes(key)) {
        delete obj[key];
      }
    });
  }
  return obj;
}

/**
 * 格式化回显城市搜索
 * @param selectedSearchCity
 */
export function getSelectedSearchCity(selectedSearchCity) {
  if (!Array.isArray(selectedSearchCity)) {
    return [];
  }
  try {
    return selectedSearchCity.map((item) => {
      const [label, value] = item.split('_');
      return {
        label: decodeURIComponent(label),
        value,
      };
    });
  } catch (error) {
    console.error(error);
    return [];
  }
}

/**
 * 处理职位详情页工作地点的展示
 */
export function cityArray2String(cityList: ICode[], cutLength?: number, separator?: string) {
  let str = '';
  if (!separator) {
    separator = '、';
  }
  if (!Array.isArray(cityList) || (Array.isArray(cityList) && cityList.length === 0)) {
    str = '';
  } else if (cutLength && cityList.length > cutLength) {
    str = `${cityList
      .slice(0, cutLength)
      .map((item) => item.name)
      .join(separator)}等`;
  } else {
    str = cityList.map((item) => item.name).join('、');
  }
  return str;
}

/**
 * 职位详情页工作地点最多展示15个字，超出“…”
 */
export function cityArray2Str(cityList: ICode[], cutLength?: number, separator?: string) {
  let str = '';
  if (!separator) {
    separator = '、';
  }
  if (!Array.isArray(cityList) || (Array.isArray(cityList) && cityList.length === 0)) {
    str = '';
  } else if (cutLength) {
    str = `${cityList
      ?.map((item) => item.name)
      ?.join(separator)
      ?.slice(0, cutLength)}...`;
  } else {
    str = cityList?.map((item) => item.name).join('、');
  }
  return str;
}

/**
 * 处理投递记录页工作地点的展示
 */
export function array2String(cityList: string[], cutLength?: number, separator?: string) {
  let str = '';
  if (!separator) {
    separator = '、';
  }
  if (!Array.isArray(cityList) || (Array.isArray(cityList) && cityList.length === 0)) {
    str = '';
  } else if (cutLength && cityList.length > cutLength) {
    str = `${cityList.slice(0, cutLength).join(separator)}等`;
  } else {
    str = cityList.join('、');
  }
  return str;
}

// 职位投递页处理source
/**
 * @param jobSource  校招、社招
 * @param referralCode 内推码
 * @param recommendNumType  是否外推码 1内推码 2外推码
 * 12-校招官网 15-校招伯乐、官网门户
 * 11-社招官网 17-社招伯乐、官网门户
 * 19-校招伯乐-外推码
 */
export function getSourceCode(isCampus, referralCode, recommendNumType = 1) {
  let source = '';
  if (!isCampus) {
    if (!referralCode) {
      source = '11';
    } else {
      source = '17';
    }
  }
  if (isCampus) {
    if (!referralCode) {
      source = '12';
    } else {
      source = '15';
    }
  }
  // 校招伯乐-外推码
  if (referralCode && recommendNumType === 2) {
    source = '19';
  }
  return source;
}

// 职位投递页处理source (目前仅限个人中心 - 创建简历)
/**
 * @param jobSource  校招、社招
 * @param referralCode 内推码
 * @param recommendNumType  是否外推码 1内推码 2外推码
 * 12-校招官网 15-校招伯乐、官网门户
 * 11-社招官网 17-社招伯乐、官网门户
 * 19-校招伯乐-外推码
 */
export function getSourceCodeForPersonCenter(
  isCampus,
  referralCode,
  recommendNumType = 1,
  ifCampusProjectIntern = false,
) {
  // 校招 - 项目实习s不会有内/外推码
  if (isCampus && ifCampusProjectIntern) return SOURCE_TYPE.campus;
  return getSourceCode(isCampus, referralCode, recommendNumType);
}

// 判断是否是校招
export function isCampusFn(jobSpecialCode) {
  return (
    jobSpecialCode === JOB_SPECIAL_CODE.YINGJIE ||
    jobSpecialCode === JOB_SPECIAL_CODE.ZHUANZHENGSHIXI ||
    jobSpecialCode === JOB_SPECIAL_CODE.BEIDOU ||
    jobSpecialCode === JOB_SPECIAL_CODE.QIHANG
  );
}

// 获取投递职位类型
export function getHighlightType(jobType, jobShareType) {
  let highlightType = '';
  if (jobShareType === JOB_SHARE_TYPE.SOCIALBOLE || jobType === JOB_TYPE.SOCIAL) {
    highlightType = 'social';
  } else if (jobType === JOB_TYPE.SHIXI || jobType === JOB_TYPE.YINGJIE) {
    highlightType = 'campus';
  }
  return highlightType;
}

// 格式化参数，空值都变成null
export function formatEmptyToNull(param) {
  Object.keys(param).forEach((key) => {
    if (param?.[key]) {
      if (param?.[key]?.length > 0) {
        for (const item of param[key]) {
          Object.keys(item).forEach((subKey) => {
            if (subKey === 'unifiedEntrance' || subKey === 'fullTimeStatus') {
              item[subKey] = item[subKey] === null || item[subKey] === '' ? null : item[subKey];
            } else if (!item[subKey]) item[subKey] = null;
          });
        }
      } else {
        Object.keys(param?.[key]).forEach((subKey) => {
          if (!param[key][subKey]) param[key][subKey] = null;
        });
      }
    }
  });
  return param;
}

// 格式化citylist，初始化增加展开折叠标识
export function formatCityList(list, selectedList: string[] = [], separator = '_') {
  if (!Array.isArray(list)) return [];
  if (list?.length === 0) return [];
  // 对selectedList去重, 只保留一级分类id
  const uniqueCodeList: string[] = [];
  if (selectedList?.length > 0) {
    selectedList?.forEach((item) => {
      const [code] = item.split(separator);
      if (!uniqueCodeList?.includes(code)) {
        uniqueCodeList?.push(code);
      }
    });
  }

  return list;
}

// CityList搜索参数处理
export const formatCityListForSearch = (list) => {
  try {
    return (list ?? []).map((item) => {
      const { code } = item;
      // 热门海外城市-处理选择全部的情况
      if (code?.includes('_-1')) {
        const [_code] = code.split('_');
        return { code: _code };
      }
      // 热门海外城市-处理选择普通二级的情况
      if (code?.includes('_')) {
        const [, subCode] = code.split('_');
        return { code: subCode };
      }
      return { code };
    });
  } catch (e) {
    console.log('formatCityListForSearch', e);
  }
};
